/* ProPreview — Free-user preview mode for Pro sections.
   Users SEE the actual UI (dimmed / non-interactive) with a clear
   sticky top ribbon and a floating bottom CTA that opens the paywall.
   Clicking anywhere on the previewed content also opens the paywall.
   Pro users see children as-is.
*/
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Lock, Sparkles, X, Check, ChevronRight } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { hapticTap } from "@/lib/haptics";

const BENEFITS = [
  "Pulsante SOS · allerta 80 km",
  "Match passeggiate compatibili",
  "Missioni: crea e partecipa",
  "Cassetto digitale + Diario",
  "Prevenzione anti-fuga",
  "Cani illimitati",
];

export default function ProPreview({ feature, title, description, children }) {
  const { isPro, canStartTrial, startTrial } = useSubscription();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  if (isPro) return children;

  const openPaywall = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault?.(); }
    hapticTap();
    setModalOpen(true);
  };

  const cta = () => {
    setModalOpen(false);
    if (canStartTrial) startTrial();
    else navigate("/billing");
  };

  return (
    <div className="relative" data-testid={`pro-preview-${feature ? feature.replace(/\s/g, "-") : "section"}`}>
      {/* Sticky Pro Ribbon */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-[550] px-3 pt-3"
      >
        <button
          onClick={openPaywall}
          data-testid="pro-ribbon-open"
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-full border text-left"
          style={{
            background: "linear-gradient(120deg, #FFF6E6 0%, #FFEDD5 60%, #FFE0F0 100%)",
            borderColor: "rgba(255,122,26,0.30)",
            boxShadow: "0 8px 22px -12px rgba(255,122,26,0.35)",
          }}
        >
          <span
            className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}
          >
            <Crown className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612]">
              Anteprima · ORIO Pro
            </div>
            <div className="text-[12.5px] text-[#1A1A1A] font-semibold truncate leading-tight">
              {title || `Sblocca ${feature || "questa sezione"}`}
            </div>
          </div>
          <span
            className="shrink-0 px-3 py-1.5 rounded-full text-white text-[11.5px] font-semibold inline-flex items-center gap-1"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)" }}
          >
            <Sparkles className="h-3 w-3" /> Sblocca
          </span>
        </button>
      </motion.div>

      {/* Preview content (children) — visible but non-interactive.
         Any click on the intercept layer opens paywall. */}
      <div className="relative">
        <div
          className="pointer-events-none select-none"
          style={{ filter: "saturate(0.92)" }}
          aria-hidden
        >
          {children}
        </div>
        {/* Click interceptor above the content */}
        <button
          type="button"
          onClick={openPaywall}
          data-testid="pro-preview-intercept"
          aria-label="Sblocca ORIO Pro"
          className="absolute inset-0 w-full h-full cursor-pointer"
          style={{
            background:
              "linear-gradient(180deg, rgba(251,247,241,0.00) 0%, rgba(251,247,241,0.12) 30%, rgba(251,247,241,0.45) 90%)",
          }}
        />
      </div>

      {/* Floating bottom CTA (mobile primary) */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[560] w-[calc(100%-24px)] max-w-md"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 180px)" }}
      >
        <motion.button
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", damping: 22, stiffness: 260 }}
          onClick={openPaywall}
          data-testid="pro-preview-cta"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full border bg-white/95"
          style={{
            borderColor: "#F0EBE3",
            backdropFilter: "blur(14px) saturate(160%)",
            WebkitBackdropFilter: "blur(14px) saturate(160%)",
            boxShadow: "0 18px 36px -10px rgba(0,0,0,0.20)",
          }}
        >
          <span
            className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 60%, #34D399 100%)" }}
          >
            <Lock className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[11.5px] font-semibold text-[#1A1A1A] leading-tight">
              {canStartTrial ? "30 giorni gratis · poi 4,99 €/mese" : "ORIO Pro · 4,99 €/mese"}
            </div>
            <div className="text-[10.5px] text-[#6B6B6B] leading-tight">
              {canStartTrial ? "Disdici quando vuoi" : "Riattiva l'abbonamento"}
            </div>
          </div>
          <span
            className="shrink-0 px-3 py-1.5 rounded-full text-white text-[11.5px] font-semibold inline-flex items-center gap-1"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)" }}
          >
            {canStartTrial ? "Inizia" : "Abbonati"}
            <ChevronRight className="h-3 w-3" />
          </span>
        </motion.button>
      </div>

      {/* Paywall modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1600] flex items-end sm:items-center justify-center px-3 pb-[180px] sm:pb-3"
            data-testid="pro-preview-modal"
            onClick={() => setModalOpen(false)}
          >
            <div className="absolute inset-0 bg-black/45 backdrop-blur-md" />
            <motion.div
              initial={{ y: 60, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 60, scale: 0.96 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="relative w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div aria-hidden className="absolute -top-24 -right-16 h-64 w-64 rounded-full opacity-30"
                style={{ background: "radial-gradient(circle, rgba(255,176,32,0.55) 0%, transparent 65%)" }} />
              <button
                onClick={() => setModalOpen(false)}
                aria-label="Chiudi"
                data-testid="pro-preview-modal-close"
                className="absolute right-3 top-3 h-8 w-8 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center z-10"
              >
                <X className="h-4 w-4 text-[#6B6B6B]" />
              </button>

              <div className="relative p-6 sm:p-7">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.20em] text-white"
                  style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}>
                  <Crown className="h-3 w-3" /> ORIO Pro
                </div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-3 leading-[1.05] text-[#1A1A1A]">
                  Sblocca<br />
                  <span className="orio-grad-text">{feature || "ORIO Pro"}.</span>
                </h2>
                <p className="text-[13.5px] text-[#6B6B6B] mt-2 leading-relaxed">
                  {description || "Il piano gratuito ti permette di esplorare l'app. Per le funzioni operative — Match, Missioni, SOS, Cassetto — serve l'abbonamento."}
                </p>

                <div className="mt-5 space-y-2">
                  {BENEFITS.map((b) => (
                    <div key={b} className="flex items-start gap-2.5 text-[13.5px]">
                      <span className="h-5 w-5 rounded-full bg-[#34D39918] flex items-center justify-center shrink-0 mt-px">
                        <Check className="h-3 w-3 text-[#2DBA60]" />
                      </span>
                      <span className="text-[#1A1A1A]">{b}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-2xl border border-[#F0EBE3] bg-[#FBF7F1] p-4">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-3xl tabular-nums text-[#1A1A1A]">4,99 €</span>
                    <span className="text-[#9A9A9A] text-sm">/ mese</span>
                  </div>
                  <div className="text-[11px] text-[#6B6B6B] mt-0.5">
                    {canStartTrial ? "30 giorni gratis · disdici quando vuoi" : "Riattiva senza vincoli · disdici quando vuoi"}
                  </div>
                </div>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={cta}
                  data-testid="pro-preview-modal-cta"
                  className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
                  style={{
                    background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                    boxShadow: "0 16px 32px -12px rgba(233,69,69,0.55)",
                  }}
                >
                  <Sparkles className="h-4 w-4" />
                  {canStartTrial ? "Inizia 30 giorni gratis" : "Abbonati a ORIO Pro"}
                </motion.button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="mt-2 w-full py-2 text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A]"
                >
                  Continua a esplorare l&apos;anteprima
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
